import React, { useEffect, useRef, useState } from 'react';
import './styles/LayerSlideNumber.scss';
import type { LayerSwitchDirection } from '../context/LayerSwitchContext';
import { NumberCounter } from './NumberCounterComponent';
import { LAYER_SLIDE_MS } from '../constants/layerSlideAnimation';
import { layerTimeDisplayNode } from '../utils/layerTimeDisplay';

type SlidePhase = 'idle' | 'exit-up' | 'exit-down' | 'enter-from-below' | 'enter-from-above';

interface ValueSlot {
  value: number;
  isNegative: boolean;
  displayAsTime: boolean;
}

interface LayerSlideNumberProps {
  value: number;
  isNegative: boolean;
  direction: LayerSwitchDirection;
  switchNonce: number;
  /** false のとき通常はスライドしない（1 層表示）。層数変化アニメ中は nonce 不一致ならスライド継続 */
  slideEnabled?: boolean;
  className?: string;
  /** 同一レイヤー内の値変化時のカウントアニメ（ms）。スライド中は 0 */
  duration?: number;
  animateFromZero?: boolean;
  suppressTooltip?: boolean;
  showTimeConversion?: boolean;
  suffix?: string;
  /** true のとき value は分数として H:MM 表示（レイヤー切替アニメも数値と同じスライド） */
  displayAsTime?: boolean;
  /** 時刻タグ（パウチ strong / フッター span 等） */
  timeDisplayTag?: 'strong' | 'span';
  formatDisplay?: (value: number, isNegative: boolean) => React.ReactNode;
}

function slideClass(phase: SlidePhase): string {
  return phase === 'idle' ? '' : `layer-slide-${phase}`;
}

/**
 * レイヤー切替: ラッパー 1 要素に exit → enter（translateY + opacity）
 * 時間↔数値モード切替も同一ラッパーでスライド。同一レイヤー内の値変化は NumberCounter のカウントアニメ
 */
export const LayerSlideNumber: React.FC<LayerSlideNumberProps> = ({
  value,
  isNegative,
  direction,
  switchNonce,
  slideEnabled = true,
  className = '',
  duration = 300,
  animateFromZero = false,
  suppressTooltip = false,
  showTimeConversion = false,
  suffix = '',
  displayAsTime = false,
  timeDisplayTag = 'span',
  formatDisplay,
}) => {
  const [display, setDisplay] = useState<ValueSlot>({
    value,
    isNegative,
    displayAsTime,
  });
  const [animPhase, setAnimPhase] = useState<SlidePhase>('idle');
  const committedNonce = useRef(switchNonce);
  const pendingSlotRef = useRef<ValueSlot>({ value, isNegative, displayAsTime });
  pendingSlotRef.current = { value, isNegative, displayAsTime };
  const isAnimating = animPhase !== 'idle';
  const slidePending =
    direction !== 0 && switchNonce === committedNonce.current;
  const slideActive = switchNonce !== committedNonce.current || isAnimating;
  const countDuration = isAnimating ? 0 : duration;
  const phaseClass = slideActive ? slideClass(animPhase) : '';
  const wrapperClass = ['layer-slide-formatted-value', className, phaseClass]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    const pending = direction !== 0 && switchNonce === committedNonce.current;
    if (!slideEnabled && !pending && switchNonce === committedNonce.current) {
      committedNonce.current = switchNonce;
      return;
    }
    if (switchNonce === committedNonce.current) return;

    if (direction === 0) {
      setDisplay(pendingSlotRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      return;
    }

    const exitPhase: SlidePhase = direction === 1 ? 'exit-up' : 'exit-down';
    const enterPhase: SlidePhase = direction === 1 ? 'enter-from-below' : 'enter-from-above';
    const targetNonce = switchNonce;

    setAnimPhase(exitPhase);

    const tEnter = window.setTimeout(() => {
      setDisplay(pendingSlotRef.current);
      setAnimPhase(enterPhase);
    }, LAYER_SLIDE_MS);

    const tIdle = window.setTimeout(() => {
      setAnimPhase('idle');
      committedNonce.current = targetNonce;
    }, LAYER_SLIDE_MS * 2);

    return () => {
      window.clearTimeout(tEnter);
      window.clearTimeout(tIdle);
    };
  }, [switchNonce, direction, slideEnabled]);

  useEffect(() => {
    if (slidePending || slideActive) return;
    setDisplay({ value, isNegative, displayAsTime });
  }, [value, isNegative, displayAsTime, switchNonce, slidePending, slideActive]);

  const renderSlotContent = (slot: ValueSlot): React.ReactNode => {
    if (slot.displayAsTime) {
      return layerTimeDisplayNode(slot.value, slot.isNegative, timeDisplayTag);
    }
    if (formatDisplay) {
      return formatDisplay(slot.value, slot.isNegative);
    }
    return (
      <NumberCounter
        value={Math.abs(slot.value)}
        isNegative={slot.isNegative}
        duration={countDuration}
        animateFromZero={animateFromZero && !isAnimating}
        suppressTooltip={suppressTooltip}
        showTimeConversion={showTimeConversion}
        suffix={suffix}
      />
    );
  };

  return (
    <span className={wrapperClass}>
      {renderSlotContent(display)}
    </span>
  );
};
